import React from 'react';
import { motion } from 'framer-motion';
import { Shield, MapPin, ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 bg-gradient-to-br from-brand-charcoal via-brand-deep-purple to-brand-charcoal overflow-hidden" data-id="element-276">
      {/* Large Bold Circular Arc Motif - RIGHT SIDE */}
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-20 pointer-events-none" data-id="element-277">
        <svg viewBox="0 0 400 400" fill="none" className="w-full h-full" data-id="element-278">
          <defs data-id="element-279">
            <linearGradient id="heroGrad1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-280">
              <stop offset="0%" stopColor="var(--colour-teal)" data-id="element-281" />
              <stop offset="50%" stopColor="var(--colour-magenta)" data-id="element-282" />
              <stop offset="100%" stopColor="var(--colour-orange)" data-id="element-283" />
            </linearGradient>
          </defs>
          <path d="M 200 40 A 160 160 0 0 1 360 200" stroke="url(#heroGrad1)" strokeWidth="32" strokeLinecap="round" data-id="element-284" />
          <path d="M 200 360 A 160 160 0 0 1 40 200" stroke="url(#heroGrad1)" strokeWidth="32" strokeLinecap="round" opacity="0.6" data-id="element-285" />
          <circle cx="200" cy="200" r="120" stroke="url(#heroGrad1)" strokeWidth="3" strokeDasharray="10 15" opacity="0.4" data-id="element-286" />
        </svg>
      </div>

      {/* Floating Particles */}
      {[...Array(4)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-brand-teal/30" style={{
      left: `${20 + i * 20}%`,
      top: `${30 + i * 15}%`
    }} animate={{
      y: [0, -20, 0],
      opacity: [0.3, 0.6, 0.3]
    }} transition={{
      duration: 3 + i,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-287" />)}

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full" data-id="element-288">
        <div className="max-w-4xl" data-id="element-289">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 border border-brand-teal/30 text-brand-teal text-sm font-medium mb-8" data-id="element-290">
            <Shield size={16} data-id="element-291" />
            <span data-id="element-292">Certified Organisational Ombuds & Mediator</span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-white leading-[1.1] mb-6" data-id="element-293">
            When a workplace issue lands on your desk and internal pathways
            aren't enough.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal via-brand-lime to-brand-magenta text-6xl md:text-7xl lg:text-8xl block mt-2" data-id="element-294">
              We're the call leaders make before it escalates.
            </span>
          </motion.h1>

          {/* Gradient Separator Line */}
          <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="h-1 w-32 bg-gradient-to-r from-brand-teal via-brand-magenta to-brand-orange mb-8 origin-left" data-id="element-295"></motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-xl md:text-2xl text-white/90 leading-relaxed mb-10 max-w-3xl font-normal" data-id="element-296">
            Independent, confidential support that helps Australian
            organisations resolve conflict, transform culture, and build
            leadership capability — before issues become formal complaints,
            legal risk, or talent loss.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-297">
            <a href="#consultation" className="inline-flex items-center justify-center bg-brand-magenta hover:bg-brand-plum text-white px-8 py-4 rounded-sm font-semibold transition-all shadow-lg shadow-brand-magenta/30 hover:shadow-xl hover:shadow-brand-magenta/40 text-center group" data-id="element-298">
              Book a confidential consultation
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-299" />
            </a>
            <a href="#services" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-brand-teal hover:bg-brand-teal/10 text-white px-8 py-4 rounded-sm font-semibold transition-all text-center" data-id="element-300">
              Explore our services
            </a>
          </motion.div>

          {/* Social Proof Line */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-white/70 text-sm mb-12 font-medium" data-id="element-301">
            Trusted by organisations across corporate, resources, government and
            community sectors for over 20 years.
          </motion.p>

          {/* Risk Reduction Line */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="flex flex-wrap items-center gap-6 text-sm text-white/90 mb-10 font-medium" data-id="element-302">
            <span className="flex items-center gap-2" data-id="element-303">
              <span className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-304"></span>
              Typically responds within 24 hours
            </span>
            <span className="flex items-center gap-2" data-id="element-305">
              <span className="w-2 h-2 rounded-full bg-brand-magenta" data-id="element-306"></span>
              No obligation
            </span>
            <span className="flex items-center gap-2" data-id="element-307">
              <span className="w-2 h-2 rounded-full bg-brand-orange" data-id="element-308"></span>
              Fully confidential
            </span>
            <span className="flex items-center gap-2" data-id="element-309">
              <span className="w-2 h-2 rounded-full bg-brand-lime" data-id="element-310"></span>
              30-minute initial call
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="pt-8 border-t border-white/20" data-id="element-311">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 text-sm font-medium" data-id="element-312">
              <span className="flex items-center gap-2 text-white/80" data-id="element-313">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" data-id="element-314"></span>
                Independent support
              </span>
              <span className="flex items-center gap-2 text-white/80" data-id="element-315">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-magenta" data-id="element-316"></span>
                Confidential pathways
              </span>
              <span className="flex items-center gap-2 text-white/80" data-id="element-317">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" data-id="element-318"></span>
                Organisational insight
              </span>
              <span className="flex items-center gap-2 text-white/80" data-id="element-319">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-lime" data-id="element-320"></span>
                Practical resolution
              </span>
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-white/80 font-medium" data-id="element-321">
              <MapPin size={16} className="text-brand-orange" data-id="element-322" />
              <span data-id="element-323">Based in Perth | Supporting organisations nationally</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}